
<template>
    <DsModal
        size="med"
        class="automation-element-list-modal"
        :title="$t('funnel.element.list.modal.title')"
        is-open
        show-close
        @close="() => $emit('close-element-list')"
    >
        <template #default>
            <DsInputField
                v-if="hasElements"
                v-model="searchString"
                autofocus
                tabindex="0"
                data-qa="search-input"
                type="search"
                :placeholder="$t('funnel.element.list.modal.searchPlaceholder')"
                @keyup.esc="() => $emit('close-element-list')"
            >
                <template #leading>
                    <DsIcon name="search" />
                </template>
            </DsInputField>

            <DsInlineAlert v-if="!hasElements" type="info">
                {{ $t('funnel.element.list.modal.noElements') }}
            </DsInlineAlert>

            <DsInlineAlert v-if="hasElements && !displayElements.length" type="info">
                {{ $t('funnel.element.list.modal.filteredElements') }}
            </DsInlineAlert>

            <DsListItem
                v-for="element in displayElements"
                :key="element.cell.id"
                data-qa="automation-element"
                clickable
                :title="element.cellTypeLabel"
                :description="element.listDisplayName"
                @click="() => onClickElement(element.cell)"
            >
                <template #leading>
                    <img
                        :src="element.iconUrl"
                        :alt="`${element.cell.type} icon`"
                        :height="24"
                        :width="24"
                    />
                </template>
            </DsListItem>
        </template>
    </DsModal>
</template>

<script>
import { getIconPathByCellType } from '../../utils/cell/automation-display-utils';
import { NOTES_TYPE, EDGE_TYPE, FLOW_TYPE } from '../../utils/editor.constants';

export default {
    props: {
        cells: {
            type: Array,
            required: true,
        },
    },
    emits: ['close-element-list', 'set-sequence-id', 'open-config-modal'],
    data() {
        return { searchString: '' };
    },
    computed: {
        hasElements() { return this.cells.length > 2; },

        displayElements() {
            return this.cells.map((cell) => {
                const searchInfo = [cell.meta.id, cell.value?.name, cell.type].join(' ');
                const listDisplayName = cell.value?.name || cell.type;
                const cellTypeLabel = this.getTypeLabel(cell.type);

                return {
                    cell,
                    searchInfo,
                    listDisplayName,
                    cellTypeLabel,
                    iconUrl: getIconPathByCellType(cell.type),
                };
            }).filter(({ cell, searchInfo }) => {
                if ([EDGE_TYPE, NOTES_TYPE].includes(cell.type)) return false;
                if (parseInt(cell.meta.id, 10) < 2) return false;
                if (this.searchString.toLocaleLowerCase() && !searchInfo.toLocaleLowerCase().includes(this.searchString)) return false;

                return true;
            });
        },
    },
    methods: {
        onClickElement(cell) {
            if (cell.type === FLOW_TYPE) {
                this.$emit('set-sequence-id', cell.meta.id);
            } else if (cell.meta.parent !== '1') {
                this.$emit('set-sequence-id', cell.meta.parent);
                this.$emit('open-config-modal', cell);
            } else {
                this.$emit('open-config-modal', cell);
            }

            this.$emit('close-element-list');
        },
        getTypeLabel(type) {
            const isWhen = this.$te(`funnel.sidebar.when.cell.${type}.label`);
            const isThen = this.$te(`funnel.sidebar.then.cell.${type}.label`);
            const isOther = this.$te(`funnel.type.${type}.label`);
            const cellTypeLabel = isWhen ? `funnel.sidebar.when.cell.${type}.label`
                : isThen ? `funnel.sidebar.then.cell.${type}.label`
                    : isOther ? `funnel.type.${type}.label` : type;

            return this.$t(cellTypeLabel);
        },
    },
};
</script>

<style lang="scss" scoped>
    .automation-element-list-modal {
        --modal-padding: 0;
        --input-margin-bottom: 0;
    }
</style>
